/**
 * The web as a write-ahead client (I-1623, ADR-096, ADR-100's 2026-10-15 amendment).
 *
 * A refresh rotates the credential on the server. If its answer never reaches the page — a tab
 * frozen or its process ended, a Mac asleep mid-request, a network change — a minted rotation leaves
 * the store holding the secret the server has just retired, and the next refresh presents it and is
 * revoked as a reuse (ADR-100 §5). That signed Jay out of Safari on 2026-10-15.
 *
 * So the page chooses its successor itself and writes it here **before** the request that names it
 * leaves. After a lost answer the store still holds the secret the server rotated to, and recovery
 * presents it.
 *
 * ⭐ **The chain.** What is held beyond the current secret is a list of candidates, oldest first:
 * each one the successor named for the one before it. Recovery presents the newest first, because
 * the newest is always safe to present: it is either the server's current secret or one the server
 * never saw, which is refused without consequence (`refresh_secret_unknown`). An older one is
 * presented only once every newer one has been refused, which proves the server never rotated past
 * it. Presenting an older one first is what would revoke: once the server has moved on, the older
 * one is a retired write-ahead secret (ADR-096 §4). See I-1626 for the bridge's two-slot version of
 * the same order.
 *
 * ⛔ **Storage lives behind this module and nowhere else.** Every refresh slot — the current secret
 * and the candidates — is read and written here, through `refreshSecretVault.ts`, which wraps each
 * value with a non-extractable AES-GCM key before it reaches `localStorage` (I-1625, ADR-100's
 * 2026-10-16 amendment). Nothing in a slot is ever plain once a write has gone through here; a plain
 * value from before the vault is wrapped on its first read.
 *
 * ⚠️ **Durability.** `localStorage` is written to disk lazily. A tab whose process is killed keeps
 * the write (the browser's storage process owns it); a power loss in the instant after the write
 * can lose it. Named as a residual in the ADR-100 amendment.
 */

import {
  RefreshSecretNotStoredError,
  RefreshSecretUnreadableError,
  heldInMemoryRefreshSecret,
  holdsStoredRefreshSecret,
  isWrappedRefreshSecret,
  readStoredRefreshSecret,
  removeStoredRefreshSecret,
  unwrapRefreshSecret,
  writeStoredRefreshSecret,
} from './refreshSecretVault';

export { RefreshSecretNotStoredError, RefreshSecretUnreadableError };

/** More than this many unconfirmed candidates means something is badly wrong; never grow past it. */
export const MAX_PENDING_REFRESH_SECRETS = 8;

/** Where the candidates for the credential under `refreshKey` are held. Per deployment, like it. */
export function pendingRefreshSecretsKey(refreshKey: string): string {
  return `${refreshKey}:pending`;
}

/**
 * The current refresh secret, plain, or `null` when none is held. Throws
 * `RefreshSecretUnreadableError` when the stored value will not unwrap: the caller ends the session
 * (ADR-100 I-1625 §4), never treats it as "never signed in".
 */
export function readCurrentRefreshSecret(refreshKey: string): Promise<string | null> {
  return readStoredRefreshSecret(refreshKey);
}

/**
 * Store `secret` as the current one, wrapped. Never rejects: with no vault it is held in memory for
 * this page (§8). Resolves once it is stored, so a caller that awaits it inside the refresh lock
 * hands the next tab the new secret, never the one just retired (§5).
 */
export function writeCurrentRefreshSecret(
  refreshKey: string,
  secret: string,
  options: { notBefore?: Promise<unknown> } = {},
): Promise<void> {
  return writeStoredRefreshSecret(refreshKey, secret, { durable: false, ...options });
}

/** Forget the current secret now, on disk and in memory. */
export function removeCurrentRefreshSecret(refreshKey: string): void {
  removeStoredRefreshSecret(refreshKey);
}

/**
 * Whether a current secret is held — on disk, in memory, or still being wrapped. Synchronous, for
 * the checks that cannot wait: `hasStoredAuthSession`, `hadCredential`, the hydrations.
 */
export function holdsCurrentRefreshSecret(refreshKey: string): boolean {
  return holdsStoredRefreshSecret(refreshKey);
}

function parseCandidates(raw: string | null): string[] {
  if (!raw) return [];
  try {
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) && parsed.every((secret) => typeof secret === 'string' && secret)
      ? (parsed as string[])
      : [];
  } catch {
    return [];
  }
}

/**
 * The unconfirmed candidates, oldest first. A list that unwraps but does not parse is no candidates
 * at all; one that will not unwrap throws `RefreshSecretUnreadableError`, because the key that wraps
 * the candidates wraps the current secret too, and the session ends (§4).
 */
export async function readPendingRefreshSecrets(refreshKey: string): Promise<string[]> {
  return parseCandidates(await readStoredRefreshSecret(pendingRefreshSecretsKey(refreshKey)));
}

/**
 * Write the candidates, wrapped, and resolve only once they are stored. Rejects with
 * `RefreshSecretNotStoredError` when the store would not keep them, or there is no vault: a secret
 * the store does not hold must never be named to the server.
 */
export async function writePendingRefreshSecrets(
  refreshKey: string,
  secrets: readonly string[],
  options: { notBefore?: Promise<unknown> } = {},
): Promise<void> {
  const key = pendingRefreshSecretsKey(refreshKey);
  if (secrets.length === 0) {
    removeStoredRefreshSecret(key);
    return;
  }
  await writeStoredRefreshSecret(key, JSON.stringify(secrets), { durable: true, ...options });
}

/**
 * T-0666 (ADR-100's T-0666 amendment, 6): put back the candidates a native vault kept, when a
 * hydration restores the session into an empty webview. Anything that is not a list of non-empty
 * strings, or is longer than the web ever holds, is no candidates: the slot is cleared. Never
 * rejects: a store that will not keep them leaves the session as the vault's current secret alone,
 * which is what a hydration restored before T-0666.
 */
export async function restorePendingRefreshSecrets(
  refreshKey: string,
  candidates: unknown,
  options: { notBefore?: Promise<unknown> } = {},
): Promise<void> {
  const valid =
    Array.isArray(candidates) &&
    candidates.length <= MAX_PENDING_REFRESH_SECRETS &&
    candidates.every((secret) => typeof secret === 'string' && secret !== '');
  if (!valid || candidates.length === 0) {
    clearPendingRefreshSecrets(refreshKey);
    return;
  }
  await writePendingRefreshSecrets(refreshKey, candidates as string[], options).catch(
    () => undefined,
  );
}

/** Forget every candidate: the rotation was confirmed, or the session is over. */
export function clearPendingRefreshSecrets(refreshKey: string): void {
  removeStoredRefreshSecret(pendingRefreshSecretsKey(refreshKey));
}

/**
 * Every refresh secret this page holds for the session — the current one and the candidates — read
 * from what the slots hold **at the moment of the call**, and unwrapped afterwards.
 *
 * For sign-out, which clears the slots in the same tick it asks for them (`signOut.ts`): the read
 * is synchronous, the unwrap is not, and the key that unwraps stays in IndexedDB after the slots are
 * cleared. A slot that will not unwrap contributes nothing; sign-out never fails on it.
 *
 * ⭐ The union of the disk and of what this page holds in memory only — a write still being wrapped,
 * or a page with no vault (§8) — so a sign-out in that moment still names the live secret.
 *
 * ⛔ *Mutation:* skip the memory peek → `signOut.test.ts`'s memory-only cases go red: a
 * sign-out while a write is queued, or with no vault, names nothing to logout.
 */
export function captureHeldRefreshSecrets(refreshKey: string): Promise<string[]> {
  const pendingKey = pendingRefreshSecretsKey(refreshKey);
  const raw = (slot: string): string | null => {
    try {
      return localStorage.getItem(slot);
    } catch {
      return null;
    }
  };
  const open = async (slot: string, stored: string | null): Promise<string | null> => {
    if (!stored) return null;
    if (!isWrappedRefreshSecret(stored)) return stored;
    try {
      return await unwrapRefreshSecret(stored, slot);
    } catch {
      return null;
    }
  };
  const inMemory = [heldInMemoryRefreshSecret(refreshKey)];
  const candidatesInMemory = heldInMemoryRefreshSecret(pendingKey);
  const current = open(refreshKey, raw(refreshKey));
  const pending = open(pendingKey, raw(pendingKey));
  return Promise.all([current, pending]).then(([secret, candidates]) => [
    ...new Set(
      [
        ...inMemory,
        secret,
        ...parseCandidates(candidatesInMemory),
        ...parseCandidates(candidates),
      ].filter((held): held is string => Boolean(held)),
    ),
  ]);
}

/** Whether this page can choose and hash its own secret (a secure context with Web Crypto). */
export function canWriteAhead(): boolean {
  return (
    typeof globalThis.crypto?.getRandomValues === 'function' &&
    typeof globalThis.crypto?.subtle?.digest === 'function'
  );
}

function base64Url(bytes: Uint8Array): string {
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/** A new 256-bit secret, base64url, as the bridge chooses one (ADR-096 §1). */
export function newRefreshSecret(): string {
  return base64Url(globalThis.crypto.getRandomValues(new Uint8Array(32)));
}

/** The base64url SHA-256 the server stores and `nextSecretHash` names. Never the secret itself. */
export async function refreshSecretHash(secret: string): Promise<string> {
  const digest = await globalThis.crypto.subtle.digest('SHA-256', new TextEncoder().encode(secret));
  return base64Url(new Uint8Array(digest));
}
